import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { AnalyticsService } from '../../shared/services/analytics.service';
import { ConversationService } from 'app/shared/services/conversation.service';

import { MessageMediaComponent } from './message-media/message-media/message-media.component';
import { PlayVoiceButtonComponent } from '../../shared/components/play-voice-button/play-voice-button.component';
import { ThumbnailLoaderComponent } from '../image-generation/thumbnail-loader/thumbnail-loader.component';
import { FeedbackControlsComponent } from '../feedback-controls/feedback-controls.component';

import { IMessage } from 'app/interfaces/conversations.interfaces';
import { IAudioState } from '../../interfaces/creator.interfaces';

import { EPlayButtonUsage } from '../../shared/enums/shared.enums';
import { EMessageItemCategory, EChatStates } from '../../enums/conversation.enums';
import { EMediaLoaderType } from '../../shared/enums/media-loader-type.enum';

import { feedbackAnimation } from '../../shared/animations/feedbackAnimation.animations';

@Component({
  selector: 'stxt-message',
  standalone: true,
  imports: [
    CommonModule,
    MessageMediaComponent,
    PlayVoiceButtonComponent,
    ThumbnailLoaderComponent,
    FeedbackControlsComponent
  ],
  templateUrl: './message.component.html',
  styleUrl: './message.component.scss',
  animations: [feedbackAnimation]
})
export class MessageComponent implements OnChanges {
  @ViewChild('playMessage') playMessage!: PlayVoiceButtonComponent;
  @Input() messageItem: IMessage;
  @Input() userId: string;
  @Input() creatorName: string;
  @Input() creatorId: string;
  @Input() voiceSupported: boolean;
  @Input() selectedMessageAudio: number;
  @Input() chatState: EChatStates;
  @Input() isFeedbackControlsEnabled: boolean;
  @Output() triggerPayment: EventEmitter<void> = new EventEmitter<void>();
  @Output() media: EventEmitter<void> = new EventEmitter<void>();
  @Output() isPreviewMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() clicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() updatePlayMessageState: EventEmitter<IAudioState> = new EventEmitter<IAudioState>();
  public mediaLoaded: boolean = false;
  public isLoadingImage: boolean = false;
  public showFeedbackControls: boolean = false;
  protected readonly EPlayButtonUsage = EPlayButtonUsage;
  protected readonly EMessageItemCategory = EMessageItemCategory;
  protected readonly EMediaLoaderType = EMediaLoaderType;

  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly conversationService: ConversationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['selectedMessageAudio'] &&
      this.selectedMessageAudio !== this.messageItem.timestamp &&
      this.selectedMessageAudio !== undefined
    ) {
      this.playMessage?.stopAudio();
    }
    this.checkMessageItemChange(changes);
  }

  checkMessageItemChange(changes: SimpleChanges): void {
    const messageItemChange = changes['messageItem'];
    if (messageItemChange && messageItemChange.currentValue) {
      const currentMessage = messageItemChange.currentValue;

      this.isLoadingImage =
        typeof currentMessage.processing === 'boolean'
          ? currentMessage.processing
          : !!currentMessage.payload?.message?.includes("I'm working on it");

      if (currentMessage.payload?.files) {
        this.checkOwnedMedia();
      }
    }
  }

  private checkOwnedMedia(): void {
    const ownedMedia = this.messageItem?.payload?.files?.some(file => file.owned);
    if (ownedMedia) {
      this.conversationService.newOwnedMediaSubject.next(this.messageItem);
    }
  }

  trackMessagePlaying(isPlaying: boolean): void {
    this.updatePlayMessageState.emit({ id: this.messageItem.timestamp, isPlaying: isPlaying });
    if (!isPlaying) return;
    this.analyticsService.voiceNotePlayed(
      this.userId,
      this.creatorName,
      EChatStates.FREEFLOW,
      this.creatorId
    );
  }
}
